import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Zap, Type, LayoutTemplate, FileUp, FileDown, Download, ShieldCheck, Check, ArrowRight, Sparkles,
  FileSignature, FileArchive, Images, MousePointerClick, Eye, FileSearch, Clock, Wallet,
} from "lucide-react";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import Footer from "../components/Footer";
import { useAuth } from "../context/AuthContext";
import heroImg from "../assets/hero.jpg";

const HERO_IMG = heroImg;

const PACKS = [
  { name: "Starter", credits: 10, price: 4.99, popular: false },
  { name: "Popular", credits: 50, price: 19.99, popular: true },
  { name: "Pro", credits: 150, price: 49.99, popular: false },
];

const CREATOR_HERO = "https://static.prod-images.emergentagent.com/jobs/d9a93d54-59f0-4c75-9778-ceb933be0089/images/61b348accefb5b5063ea1cf50fb1627ad8f8414bdfd88fe76d56e65a0f97106b.jpeg";
const CREATOR_SECONDARY = "https://static.prod-images.emergentagent.com/jobs/d9a93d54-59f0-4c75-9778-ceb933be0089/images/db28f931165855a4f240edddfa060ab0bec14782722ac07260b0aadcb48e8cd5.jpeg";

const EMERALD = "bg-emerald-50 text-emerald-700 border-emerald-200";
const INDIGO = "bg-indigo-50 text-indigo-700 border-indigo-200";
const AMBER = "bg-amber-50 text-amber-800 border-amber-200";

const SHOWCASE_TOOLS = [
  { name: "Rich Text", Icon: Type, cat: "Create", desc: "Write & format polished PDFs.", badge: "1 credit", badgeColor: EMERALD, testid: "home-tool-rich-text", toolId: "editor" },
  { name: "Templates", Icon: LayoutTemplate, cat: "Create", desc: "Invoices, resumes, certificates.", badge: "1 credit", badgeColor: EMERALD, testid: "home-tool-templates", toolId: "templates" },
  { name: "File → PDF", Icon: FileUp, cat: "Convert", desc: "Word, Excel, PPT & images to PDF.", badge: "from 1 credit", badgeColor: INDIGO, testid: "home-tool-file-to-pdf", toolId: "converter" },
  { name: "PDF → Word / Images", Icon: FileDown, cat: "Convert", desc: "Extract editable docs or images.", badge: "2 credits", badgeColor: INDIGO, testid: "home-tool-pdf-to-word", toolId: "reverse" },
  { name: "Make Searchable", Icon: FileSearch, cat: "Convert", desc: "OCR scans into searchable text.", badge: "OCR · 3 credits", badgeColor: AMBER, testid: "home-tool-ocr", toolId: "searchable" },
  { name: "Agreements", Icon: FileSignature, cat: "Creator", desc: "Draft contracts, NDAs & letters.", badge: "1 credit", badgeColor: EMERALD, testid: "home-tool-agreements", toolId: "agreements" },
  { name: "Compress", Icon: FileArchive, cat: "Creator", desc: "Shrink PDFs for email & web.", badge: "1 credit", badgeColor: EMERALD, testid: "home-tool-compress", toolId: "compress" },
  { name: "Social Carousel", Icon: Images, cat: "Creator", desc: "PDF into IG / LinkedIn slides.", badge: "2 credits", badgeColor: INDIGO, testid: "home-tool-carousel", toolId: "carousel" },
];

const PILLARS = [
  { Icon: Wallet, title: "Credits you own", desc: "Pay per use — no subscription, no cancel reminders." },
  { Icon: Clock, title: "24-hour auto-delete", desc: "Every file is permanently purged 24h after you make it." },
  { Icon: Eye, title: "Preview before you pay", desc: "See a watermarked preview; spend a credit only to unlock." },
];

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.5, ease: "easeOut" } }),
};

export default function Landing() {
  const { user, login } = useAuth();
  const cta = user ? "/studio" : null;

  // Capture a referral code from ?ref= so it survives Google sign-in.
  useEffect(() => {
    const ref = new URLSearchParams(window.location.search).get("ref");
    if (ref) {
      try { localStorage.setItem("facio_ref", ref); } catch (_) {}
    }
  }, []);

  const startBtn = (label, testid) =>
    user ? (
      <Link to="/studio" data-testid={testid}>
        <Button className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full px-7 h-12 text-base">
          {label} <ArrowRight className="ml-1.5 h-4 w-4" />
        </Button>
      </Link>
    ) : (
      <Button onClick={login} data-testid={testid} className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full px-7 h-12 text-base">
        {label} <ArrowRight className="ml-1.5 h-4 w-4" />
      </Button>
    );

  const toolCard = (t) => {
    const cls = "group relative flex flex-col justify-between p-5 bg-white/90 backdrop-blur-md rounded-2xl border border-slate-200/80 shadow-sm hover:shadow-xl hover:shadow-indigo-500/10 hover:border-indigo-400/60 hover:-translate-y-1 transition-all duration-200 text-left overflow-hidden";
    const inner = (
      <>
        <span className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-indigo-500 via-indigo-600 to-indigo-400 opacity-0 group-hover:opacity-100 transition-opacity" />
        <div>
          <div className="w-11 h-11 rounded-xl bg-indigo-50/80 text-indigo-600 group-hover:bg-indigo-600 group-hover:text-white flex items-center justify-center transition-colors mb-4">
            <t.Icon className="h-5 w-5" />
          </div>
          <h3 className="font-display text-base font-semibold text-slate-900">{t.name}</h3>
          <p className="text-sm text-slate-500 mt-1 leading-relaxed">{t.desc}</p>
        </div>
        <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-100">
          <span className="text-[10px] uppercase tracking-widest text-slate-400 font-mono">{t.cat}</span>
          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-mono font-medium border ${t.badgeColor}`}>{t.badge}</span>
        </div>
      </>
    );
    return user
      ? <Link key={t.name} to={`/studio?tool=${t.toolId}`} data-testid={t.testid} className={cls}>{inner}</Link>
      : <button key={t.name} onClick={() => { localStorage.setItem("facio_open_tool", t.toolId); login(); }} data-testid={t.testid} className={cls}>{inner}</button>;
  };

  return (
    <div className="min-h-screen grain">
      <Navbar />

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-16 pb-20 grid lg:grid-cols-2 gap-12 items-center">
        <motion.div initial="hidden" animate="show" variants={fade}>
          <div className="inline-flex items-center gap-2 bg-indigo-50 text-indigo-700 text-xs font-mono uppercase tracking-widest px-3 py-1.5 rounded-full mb-6">
            <Sparkles className="h-3.5 w-3.5" /> Pay-as-you-go PDF & Document Tools
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
            Create, convert & polish <span className="text-indigo-600">PDFs</span> in seconds.
          </h1>
          <p className="mt-5 text-lg text-slate-600 max-w-lg leading-relaxed">
            Write documents, fill templates, convert files, compress, OCR and export social carousels.
            You only pay for what you make — <span className="font-semibold text-slate-900">from 1 credit</span>, no subscription.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            {startBtn("Start creating", "hero-start-button")}
            <a href="#pricing" className="text-slate-700 font-medium hover:text-indigo-600 transition-colors flex items-center gap-1">
              See pricing
            </a>
          </div>
          <div className="mt-8 flex items-center gap-6 text-sm text-slate-500">
            <span className="flex items-center gap-1.5"><Check className="h-4 w-4 text-emerald-500" /> 3 free credits on signup</span>
            <span className="flex items-center gap-1.5"><Check className="h-4 w-4 text-emerald-500" /> No subscription</span>
          </div>
        </motion.div>

        <motion.div initial="hidden" animate="show" variants={fade} custom={1} className="relative">
          <div className="absolute -inset-4 bg-indigo-600/10 rounded-3xl blur-2xl" />
          <div className="relative rounded-2xl overflow-hidden border border-slate-200 paper-shadow bg-white">
            <img src={HERO_IMG} alt="Facio Doc showing a PDF document on a laptop and phone" className="w-full h-[420px] object-cover" />
          </div>
          <div className="absolute -bottom-5 -left-5 bg-white rounded-xl border border-slate-200 paper-shadow px-4 py-3 flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-emerald-100 flex items-center justify-center">
              <Download className="h-4 w-4 text-emerald-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500 font-mono">EXPORTED</p>
              <p className="text-sm font-semibold">invoice_2026.pdf</p>
            </div>
          </div>
        </motion.div>
      </section>

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-4 pb-8">
        <h2 className="font-display text-xl font-bold tracking-tight text-center mb-5">How it works</h2>
        <div className="grid sm:grid-cols-3 gap-4">
          {[
            { Icon: MousePointerClick, step: "01", title: "Pick a tool", desc: "Create, convert or use a Creator Tool." },
            { Icon: Eye, step: "02", title: "See the cost", desc: "Every tool shows its exact credit cost up front." },
            { Icon: Download, step: "03", title: "Approve & download", desc: "Export instantly, or straight to your Google Drive." },
          ].map((s) => (
            <div key={s.step} className="bg-white rounded-2xl border border-slate-200 p-5 flex items-start gap-4">
              <div className="h-11 w-11 rounded-xl bg-indigo-100 flex items-center justify-center shrink-0"><s.Icon className="h-5 w-5 text-indigo-600" /></div>
              <div>
                <div className="text-xs font-mono text-slate-400">{s.step}</div>
                <h3 className="font-display font-semibold">{s.title}</h3>
                <p className="text-slate-600 text-sm mt-0.5">{s.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Tools showcase */}
      <section id="tools" data-testid="homepage-tools-showcase" className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <div className="text-center">
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
            A whole studio of document tools
          </h2>
          <p className="text-center text-slate-600 mt-3 max-w-xl mx-auto">
            Everything creators need to make, convert and share documents — and you only pay for what you export.
          </p>
        </div>

        {/* Lifestyle photo + live Studio preview */}
        <div className="mt-12 grid lg:grid-cols-2 gap-6 items-stretch">
          <motion.div
            initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade}
            className="relative rounded-3xl overflow-hidden border border-slate-200 paper-shadow min-h-[380px]"
          >
            <img src={CREATOR_HERO} alt="A content creator working on documents at a bright studio desk" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/75 via-slate-900/20 to-transparent" />
            <div className="absolute bottom-0 left-0 p-8 text-white">
              <p className="font-display text-2xl font-semibold max-w-sm leading-snug">Built for creators who ship every day</p>
              <p className="text-white/85 text-sm mt-2 max-w-md leading-relaxed">
                Proposals, invoices, carousels and contracts — without the monthly subscription tax.
              </p>
            </div>
          </motion.div>

          {/* Browser-framed Studio snapshot (real UI) */}
          <motion.div
            initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade}
            className="rounded-3xl overflow-hidden border border-slate-200 paper-shadow bg-white flex flex-col"
          >
            <div className="flex items-center gap-1.5 px-4 py-3 bg-slate-100 border-b border-slate-200">
              <span className="w-3 h-3 rounded-full bg-red-400" />
              <span className="w-3 h-3 rounded-full bg-amber-400" />
              <span className="w-3 h-3 rounded-full bg-emerald-400" />
              <span className="ml-3 text-[11px] text-slate-400 font-mono truncate">faciodoc.com/studio</span>
            </div>
            <div className="p-6 grain flex-1">
              <p className="font-display text-lg font-bold text-slate-900">Creator Studio</p>
              <p className="text-xs text-slate-500 mb-4">Pick a tool — you only pay when you unlock a file.</p>
              <div className="grid grid-cols-2 gap-3">
                {SHOWCASE_TOOLS.slice(0, 4).map((t) => (
                  <div key={t.name} className="p-3 bg-white rounded-xl border border-slate-200/80 shadow-sm">
                    <div className="w-9 h-9 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center mb-2">
                      <t.Icon className="h-4 w-4" />
                    </div>
                    <p className="text-sm font-semibold text-slate-900 leading-tight">{t.name}</p>
                    <span className={`mt-2 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-mono font-medium border ${t.badgeColor}`}>{t.badge}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>

        {/* Value pillars */}
        <div className="mt-6 grid sm:grid-cols-3 gap-4">
          {PILLARS.map((p) => (
            <div key={p.title} className="bg-white/90 backdrop-blur-md rounded-2xl border border-slate-200/80 p-5 flex items-start gap-4">
              <div className="h-11 w-11 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0">
                <p.Icon className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-display font-semibold text-slate-900">{p.title}</h3>
                <p className="text-slate-600 text-sm mt-0.5 leading-relaxed">{p.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Tool grid with credit badges */}
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {SHOWCASE_TOOLS.map((t) => toolCard(t))}
        </div>

        <div className="mt-10 text-center">
          {startBtn("Explore the Studio", "tools-showcase-cta")}
          <p className="text-slate-500 text-sm mt-3">3 free credits on signup · no subscription · files auto-delete in 24h</p>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 bg-slate-900 text-white text-xs font-mono uppercase tracking-widest px-3 py-1.5 rounded-full mb-4">
            <Zap className="h-3.5 w-3.5 text-amber-400" /> Credit Packs
          </div>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Simple, honest pricing</h2>
          <p className="text-slate-600 mt-3">Simple jobs from 1 credit, heavier jobs a little more — always shown up front. Credits never expire.</p>
          <Link to="/pricing" data-testid="landing-pricing-link" className="inline-flex items-center gap-1 mt-3 text-indigo-600 font-medium hover:underline">
            See full pricing & limits <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="mt-12 grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          {PACKS.map((p) => (
            <div
              key={p.name}
              className={`relative bg-white rounded-2xl border p-8 flex flex-col ${
                p.popular ? "border-indigo-600 ring-2 ring-indigo-600 shadow-xl md:-translate-y-2" : "border-slate-200"
              }`}
            >
              {p.popular && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-indigo-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                  Most Popular
                </span>
              )}
              <h3 className="font-display text-lg font-semibold">{p.name}</h3>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="font-display text-4xl font-bold">${p.price}</span>
              </div>
              <p className="text-slate-500 mt-1 font-mono text-sm">{p.credits} credits</p>
              <p className="text-slate-400 text-xs mt-1">${(p.price / p.credits).toFixed(2)} per PDF</p>
              <div className="mt-6 flex-1 space-y-2 text-sm text-slate-600">
                <p className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-500" /> {p.credits} PDF exports</p>
                <p className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-500" /> All tools & templates</p>
                <p className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-500" /> Credits never expire</p>
              </div>
              {startBtn("Get started", `pricing-cta-${p.name.toLowerCase()}`)}
            </div>
          ))}
        </div>
      </section>

      {/* Trust */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-20">
        <div className="bg-slate-900 rounded-3xl px-8 py-14 text-center text-white">
          <ShieldCheck className="h-10 w-10 text-indigo-400 mx-auto mb-4" />
          <h2 className="font-display text-2xl sm:text-3xl font-bold">Your documents, your control</h2>
          <p className="text-slate-300 mt-3 max-w-xl mx-auto">
            Secure sign-in with Google, private document history, and instant downloads whenever you need them.
          </p>
          <div className="mt-8">{startBtn("Create your first PDF", "footer-cta-button")}</div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
